import moment from 'moment';
import 'moment/locale/pt-br';
import { Provider } from 'react-redux';
import React, { Suspense } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { BrowserRouter } from 'react-router-dom';

// Seta locale globalmente do moment. O `import 'moment/locale/pt-br'` só
// registra; precisa do `moment.locale('pt-br')` pra ATIVAR como default.
// Sem isso, o `@mui/x-date-pickers` v9 renderiza datas em inglês mesmo
// com `adapterLocale="pt-br"` no LocalizationProvider.
moment.locale('pt-br');

/**
 * Detecta o basename do React Router baseado na URL atual.
 *
 * No coruja v2 standalone, cada app vive em /src/apps/<nome>/ (no Vite),
 * mas o React Router das apps espera estar montado em /. Sem basename,
 * todas as rotas internas batem com Page404.
 *
 * Esta função detecta o prefixo do path e retorna como basename, fazendo
 * o BrowserRouter ignorar a parte /src/apps/<X> na URL.
 */
function detectBasename() {
  if (typeof window === 'undefined') return '';
  const m = window.location.pathname.match(/^(\/src\/apps\/[^/]+)/);
  return m ? m[1] : '';
}

import { CssBaseline, ThemeProvider, StyledEngineProvider, CircularProgress } from '@mui/material';
// `@mui/styles` (legacy makeStyles/withStyles) precisa do seu próprio
// ThemeProvider pra que `theme.spacing()`, `theme.breakpoints`, etc
// funcionem dentro dos callbacks `styles(theme)`.
import { ThemeProvider as LegacyThemeProvider } from '@mui/styles';
import ErrorBoundary from '../../applets/error/ErrorBoundary';
import Notifier from './Notifier';
import RootComponent from './RootComponent';
import store from '../../core/store';
import { theme } from '../../theme';

const appDB = store.get();

function Loading() {
  return (
    <div
      style={{ display: 'flex', justifyContent: 'center', padding: '100 0' }}
    >
      <CircularProgress />
    </div>
  );
}

/**
 * Componente, que deve ser posto na raiz de aplicação
 * (pública e não pública), responsável por, principalmente,
 * configura e prover o storage do redux, configurar o tema
 * do Material-UI, exibir o `SnackBar` que informa se a
 * aplicação está se comunicando com o backend e requisitar
 * as configurações do usuário à api.
 */
function AppRoot(props) {
  const { children } = props;

  return (
    <Provider store={appDB}>
      <CssBaseline>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <LegacyThemeProvider theme={theme}>
              <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale="pt-br"
                dateLibInstance={moment}
              >
                <BrowserRouter basename={detectBasename()}>
                  <RootComponent {...props}>
                    <Suspense fallback={<Loading />}>
                      <ErrorBoundary>{children}</ErrorBoundary>
                    </Suspense>
                  </RootComponent>
                </BrowserRouter>
                <Notifier />
              </LocalizationProvider>
            </LegacyThemeProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </CssBaseline>
    </Provider>
  );
}

export default AppRoot;
